'use client';

import { useEffect, useState, useCallback } from 'react';

export type Theme = 'dark' | 'light';

export default function useTheme(): [Theme, (theme: Theme) => void] {
  const [theme, setThemeState] = useState<Theme>('dark');

  // 내부적으로 setState + 쿠키 저장
  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState((prev) => {
      if (prev === newTheme) return prev;
      setCookie('theme', newTheme);
      return newTheme;
    });
  }, []);

  // 초기 설정
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const cookieTheme = getCookie('theme') as Theme | null;
    if (cookieTheme === 'dark' || cookieTheme === 'light') {
      setTheme(cookieTheme);
    } else {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const systemTheme: Theme = prefersDark ? 'dark' : 'light';
      setTheme(systemTheme);
    }
  }, [setTheme]);

  // 시스템 테마 변경 감지 (쿠키 없을 때만)
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      const cookieTheme = getCookie('theme');
      if (!cookieTheme) {
        const newTheme: Theme = e.matches ? 'dark' : 'light';
        setTheme(newTheme);
      }
    };
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [setTheme]);

  return [theme, setTheme];
}

// utils
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}
function setCookie(name: string, value: string) {
  document.cookie = `${name}=${value}; path=/; max-age=31536000`;
}